import { getElement, PageStatus, normalizeForSearch, renderSortMenu } from "./generics";
import { sortKolaNaukowe, renderKoloNaukowe, KoloNaukowe, KoloNaukoweSortField } from "./kolaNaukowe";
import { api } from "./api";
import { renderAddKoloNaukoweButton } from "./addKoloNaukowe";

// Odpowiednik subjectBrowser.ts, dla katalogu kół naukowych - znacznie okrojony: bez kategorii/
// semestru (koła naukowe ich nie mają, patrz KoloNaukowe w models.py) i bez przycisku "Dodaj
// koło" (lista jest zarządzana administracyjnie/importem, nie przez userów - patrz komentarz
// przy modelu).
const searchInput      = getElement<HTMLInputElement>("search-input");
const koloList          = getElement<HTMLDivElement>("kolo-naukowe-list");
const statusBar         = getElement<HTMLParagraphElement>("status-bar");
const sortMenuContainer = getElement<HTMLDivElement>("sort-menu-container");
const browseInfo        = getElement<HTMLDivElement>("browse-kola-naukowe-info");

const universitySlug: string = browseInfo.dataset.universitySlug!;
const facultySlug: string = browseInfo.dataset.facultySlug!;
const facultyId: number = Number(browseInfo.dataset.facultyId!);

const SORT_STORAGE_KEY = "mumin_browse_kola_naukowe_sort";
const VALID_SORTS: KoloNaukoweSortField[] = ["most_comments", "name", "rating"];
const SORT_OPTIONS: { value: KoloNaukoweSortField, label: string }[] = [
    { value: "most_comments", label: "Najwięcej opinii" },
    { value: "name", label: "Nazwa" },
    { value: "rating", label: "Ocena" },
];

function loadSavedSort(): KoloNaukoweSortField {
    try {
        const saved = localStorage.getItem(SORT_STORAGE_KEY);
        if (saved && (VALID_SORTS as string[]).includes(saved)) return saved as KoloNaukoweSortField;
    } catch { /* ignore */ }
    return "most_comments";
}

function saveSort(sortBy: KoloNaukoweSortField): void {
    try { localStorage.setItem(SORT_STORAGE_KEY, sortBy); } catch { /* ignore */ }
}

interface AppState {
    kola: KoloNaukowe[];
    status: PageStatus;
    error: string;
    sortBy: KoloNaukoweSortField;
    visibleCount: number;
}

let state: AppState = {
    kola: [],
    status: "idle",
    error: "",
    sortBy: loadSavedSort(),
    visibleCount: 20,
};

const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
        state.visibleCount += 20;
        render();
    }
}, { rootMargin: "100px" });

function render(): void {
    observer.disconnect();
    koloList.innerHTML = "";

    if (state.status === "loading") {
        statusBar.textContent = "Ładowanie...";
        return;
    }
    if (state.status === "error") {
        statusBar.textContent = `Error: ${state.error}`;
        return;
    }

    const query = normalizeForSearch(searchInput.value);
    let filtered = state.kola;
    if (query) {
        filtered = filtered.filter(k => normalizeForSearch(k.name).includes(query));
    }

    const sorted = sortKolaNaukowe(filtered, query ? "name" : state.sortBy);
    statusBar.textContent = query && sorted.length > 0 ? `${sorted.length} znalezionych wyników` : "";

    const visible = sorted.slice(0, state.visibleCount);
    visible.forEach(kolo => {
        koloList.appendChild(renderKoloNaukowe(kolo, universitySlug, facultySlug));
    });

    const reachedEnd = state.visibleCount >= sorted.length;
    if (!reachedEnd) {
        const sentinel = document.createElement("div");
        sentinel.id = "scroll-sentinel";
        sentinel.style.height = "20px";
        koloList.appendChild(sentinel);
        observer.observe(sentinel);
    }

    // Mały przycisk "Nie znalazłeś koła naukowego?" pod wynikami wyszukiwania - TYLKO tutaj, bez
    // dużego, zawsze widocznego CTA gdzie indziej na stronie (patrz addKoloNaukowe.ts) - ten sam
    // wzorzec co "Nie znalazłeś przedmiotu?"/"Nie znalazłeś wykładowcy?" w subjectBrowser.ts/
    // browser.ts.
    if (query) {
        const prompt = document.createElement("div");
        prompt.className = "add-lecturer-prompt";
        const question = document.createElement("p");
        question.className = "add-lecturer-question";
        question.textContent = "Nie znalazłeś koła naukowego?";
        prompt.appendChild(question);
        koloList.appendChild(prompt);
        renderAddKoloNaukoweButton(prompt, facultyId, () => searchInput.value);
    } else if (state.status === "success" && sorted.length === 0) {
        const empty = document.createElement("div");
        empty.className = "empty-faculty-state";
        const heading = document.createElement("p");
        heading.className = "empty-faculty-heading";
        heading.textContent = "Nie znaleziono żadnych kół naukowych.";
        empty.append(heading);
        koloList.appendChild(empty);
    }
}

async function loadKolaNaukowe(): Promise<void> {
    state = { ...state, status: "loading" };
    render();

    const res = await api.getKolaNaukowe(facultyId);
    if (!res.ok) {
        state = { ...state, status: "error", error: res.error };
        render();
        return;
    }

    state = { ...state, kola: res.data.kola_naukowe, status: "success", visibleCount: 20 };
    render();
}

const sortMenu = renderSortMenu(SORT_OPTIONS, state.sortBy, (sortBy) => {
    saveSort(sortBy);
    state = { ...state, sortBy, visibleCount: 20 };
    render();
}, "Sortuj");
sortMenuContainer.appendChild(sortMenu.element);

loadKolaNaukowe();

window.addEventListener("pageshow", (event) => {
    if (event.persisted) loadKolaNaukowe();
});

searchInput.addEventListener("input", () => {
    state.visibleCount = 20;
    render();
});
